import type { SectionResult } from "@shared/labs/level-assessment";
import type { ModelAxis } from "@shared/labs/level-assessment-model";
import { Boxes, Building2, HelpCircle, type LucideIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Radar, type RadarAxis } from "./radar";
import { C, DM, UI } from "./ui-tokens";

type VerdictTone = "success" | "warning" | "neutral";

/** Explains the per-axis `· NN%` plotted beside each competency on the radar. */
const CONFIDENCE_HINT =
  "A porcentagem indica a confiança na pontuação de cada competência — quanto maior, mais sustentada pelas evidências.";

function sectionVerdict(section: SectionResult): { tone: VerdictTone; label: string } {
  const nd = section.abstainedAxes.length;
  if (nd > 0) return { tone: "warning", label: `${nd} competência${nd === 1 ? "" : "s"} N/D` };
  if (section.confidence >= 0.75) return { tone: "success", label: "Forte" };
  return { tone: "neutral", label: "Parcial" };
}

function Badge({ tone, children }: { tone: VerdictTone; children: ReactNode }) {
  const map: Record<VerdictTone, { bg: string; fg: string }> = {
    success: { bg: C.greenBg, fg: C.greenFg },
    warning: { bg: C.amberBg, fg: C.amberFg },
    neutral: { bg: C.paper, fg: C.ink },
  };
  const s = map[tone];
  return (
    <span
      style={{
        ...UI,
        display: "inline-flex",
        alignItems: "center",
        fontWeight: 500,
        fontSize: 12,
        lineHeight: "16px",
        padding: "3px 10px",
        borderRadius: 999,
        background: s.bg,
        color: s.fg,
        border: tone === "neutral" ? `1px solid ${C.border}` : "none",
      }}
    >
      {children}
    </span>
  );
}

/** A `?` affordance with a hover/focus tooltip. Used to explain the per-axis
 *  confidence percentages plotted on the radar. */
function HelpTip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        aria-label={text}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          border: "none",
          background: "transparent",
          color: C.ink50,
          cursor: "help",
        }}
      >
        <HelpCircle size={15} />
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            ...UI,
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 1,
            width: 220,
            padding: "8px 10px",
            borderRadius: 8,
            background: C.ink,
            color: C.white,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.45,
            boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

function InstrumentCard({
  icon: Icon,
  title,
  subtitle,
  verdict,
  help,
  children,
}: {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  verdict?: { tone: VerdictTone; label: string };
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        padding: 24,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            width: 34,
            height: 34,
            flex: "none",
            borderRadius: 9,
            background: C.red10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={19} color={C.red} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              ...DM,
              fontWeight: 700,
              fontSize: 17,
              letterSpacing: "-0.01em",
              color: C.ink,
              margin: 0,
            }}
          >
            {title}
          </h3>
          {subtitle && (
            <div
              style={{
                ...UI,
                fontSize: 13,
                fontWeight: 500,
                color: C.ink50,
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        {help}
        {verdict && <Badge tone={verdict.tone}>{verdict.label}</Badge>}
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingTop: 8,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function toRadarAxes(section: SectionResult, axes: ModelAxis[]): RadarAxis[] {
  const labelById = new Map(axes.map((a) => [a.id, a.label]));
  return section.axes.map((a) => ({
    id: a.axis,
    label: labelById.get(a.axis) ?? a.axis,
    level: a.inferredLevel,
    confidence: a.confidence,
  }));
}

/** The discipline's sub-band, but only when it is a proper subset of the model
 *  range — a discipline spanning the full ladder has no meaningful band to
 *  shade, so it degrades to `undefined` (no region, brand-red markers), exactly
 *  like the cross radar. */
function verticalBand(
  disciplineLevels: { min: number; max: number } | undefined,
  levels: { min: number; max: number },
): { min: number; max: number } | undefined {
  if (!disciplineLevels) return undefined;
  const isSubset = disciplineLevels.min > levels.min || disciplineLevels.max < levels.max;
  return isSubset ? disciplineLevels : undefined;
}

export interface ControlPanelProps {
  result: LevelAssessmentResultLike;
  selectedId: string | null;
  onSelect: (id: string) => void;
  showConfidence?: boolean;
}

// Local structural type — avoids importing the full result type signature here.
interface LevelAssessmentResultLike {
  cross: SectionResult;
  vertical: SectionResult;
  modelSnapshot: {
    crossAxes: ModelAxis[];
    discipline: {
      label: string;
      axes: ModelAxis[];
      /** The discipline's level sub-band (absent = full model range). Drives
       *  the radar's shaded band region + out-of-band marker for vertical
       *  competencies (suppressed when it equals the full model range). */
      levels?: { min: number; max: number };
    } | null;
    levels: { min: number; max: number };
  };
}

/** Two side-by-side radar instruments — Cross and the targeted discipline's
 *  Vertical competencies. The vertical card degrades to an empty-state when the
 *  run had no discipline. */
export function ControlPanel({ result, selectedId, onSelect, showConfidence }: ControlPanelProps) {
  const { crossAxes, discipline, levels } = result.modelSnapshot;
  const hasVertical = discipline !== null && result.vertical.axes.length > 0;
  // Roll-up of competencies that fell below this function's level range — the
  // out-of-band weakness, surfaced at the section level so it isn't buried in a
  // single spoke.
  const belowCount = result.vertical.axes.filter((a) => a.bandStatus === "below").length;

  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "stretch" }}
    >
      <InstrumentCard
        icon={Building2}
        title="Competências Cross"
        verdict={sectionVerdict(result.cross)}
        help={showConfidence ? <HelpTip text={CONFIDENCE_HINT} /> : undefined}
      >
        <Radar
          axes={toRadarAxes(result.cross, crossAxes)}
          levels={levels}
          selectedId={selectedId}
          onSelect={onSelect}
          showConfidence={showConfidence}
        />
      </InstrumentCard>

      <InstrumentCard
        icon={Boxes}
        title="Competências Verticais"
        subtitle={discipline?.label}
        verdict={hasVertical ? sectionVerdict(result.vertical) : undefined}
        help={showConfidence && hasVertical ? <HelpTip text={CONFIDENCE_HINT} /> : undefined}
      >
        {hasVertical ? (
          <>
            <Radar
              axes={toRadarAxes(result.vertical, discipline.axes)}
              levels={levels}
              band={verticalBand(discipline.levels, levels)}
              selectedId={selectedId}
              onSelect={onSelect}
              showConfidence={showConfidence}
            />
            {belowCount > 0 && (
              <p
                style={{
                  ...UI,
                  fontSize: 12.5,
                  color: C.amberFg,
                  lineHeight: 1.5,
                  textAlign: "center",
                  margin: "4px 0 0",
                  maxWidth: 320,
                }}
              >
                {belowCount === 1
                  ? "1 competência abaixo da faixa desta função."
                  : `${belowCount} competências abaixo da faixa desta função.`}
              </p>
            )}
          </>
        ) : (
          <p
            style={{
              ...UI,
              fontSize: 13,
              color: C.ink50,
              lineHeight: 1.55,
              textAlign: "center",
              maxWidth: 320,
            }}
          >
            Esta avaliação não inclui competências verticais. Selecione uma disciplina ao avaliar
            para ver esta seção.
          </p>
        )}
      </InstrumentCard>
    </div>
  );
}
